import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageMeta } from '../hooks/usePageMeta';

const HEADER_IMG = '/bracketsim_header.png';

const Pricing: React.FC = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  usePageMeta({
    title: 'Pricing — My Bracket Sim',
    description: 'See pricing for My Bracket Sim Pro. One-time $5 payment for the entire tournament season — simulations, rooting interests, and more.',
    path: '/pricing',
  });

  const faqs = [
    {
      question: 'Is this a subscription?',
      answer:
        'No. Pro is a one-time $5 payment for the entire tournament season. No recurring charges.',
    },
    {
      question: 'What does simulation do?',
      answer:
        "It runs through possible tournament outcomes using live odds to calculate each player's probability of winning your pool. Data-driven projections instead of guesswork.",
    },
    {
      question: 'Can I use it with my existing pool?',
      answer:
        'Yes. Import directly from ESPN, CBS, or Yahoo bracket games, or enter picks manually.',
    },
  ];

  return (
    <div style={s.page}>
      <header style={s.header}>
        <img
          src={HEADER_IMG}
          alt="BracketSim"
          style={s.logo}
          onClick={() => navigate('/')}
        />
        <nav style={s.nav}>
          <a href="/how-it-works" style={s.navLink}>How It Works</a>
          <a href="/login" className="pr-login-link" style={s.navAccent}>Log In</a>
        </nav>
      </header>

      <section style={s.hero}>
        <div style={s.heroInner}>
          <span style={s.label}>{'{ '}PRICING{' }'}</span>
          <h1 style={s.title}>Get started for free. One payment unlocks everything.</h1>
        </div>
      </section>

      <section style={s.cards}>
        <div style={s.cardsGrid}>
          {/* Free tier */}
          <div style={s.freeCard}>
            <h3 style={s.tierName}>Free</h3>
            <div style={s.priceBlock}>
              <span style={s.priceNum}>$0</span>
            </div>
            <ul style={s.feats}>
              <li style={s.feat}>
                <span style={s.arrow}>&rarr;</span> Scenario generator
              </li>
              <li style={s.feat}>
                <span style={s.arrow}>&rarr;</span> Import from ESPN, CBS &amp; Yahoo
              </li>
              <li style={s.feat}>
                <span style={s.arrow}>&rarr;</span> Custom scoring by round
              </li>
              <li style={s.feat}>
                <span style={s.arrow}>&rarr;</span> Save brackets &amp; scenarios
              </li>
            </ul>
            <button
              style={s.freeBtn}
              className="pr-free-btn"
              onClick={() => navigate('/login')}
            >
              Get Started
            </button>
          </div>

          {/* Pro tier */}
          <div style={s.proCard}>
            <h3 style={s.proTier}>Pro</h3>
            <div style={s.proPrice}>
              <span style={s.proPriceNum}>$5</span>
              <span style={s.proPriceLabel}>one-time</span>
            </div>
            <ul style={s.proFeats}>
              <li style={s.proFeat}>
                <span style={s.proArrow}>&rarr;</span> Everything in Free
              </li>
              <li style={s.proFeat}>
                <span style={s.proArrow}>&rarr;</span> Unlimited simulations with live odds
              </li>
              <li style={s.proFeat}>
                <span style={s.proArrow}>&rarr;</span> Per-player rooting interests
              </li>
              <li style={s.proFeat}>
                <span style={s.proArrow}>&rarr;</span> Ad-free experience
              </li>
            </ul>
            <button
              style={s.proBtn}
              className="pr-pro-btn"
              onClick={() => navigate('/login')}
            >
              Upgrade to Pro
            </button>
          </div>
        </div>
      </section>

      <section style={s.faqSection}>
        <span style={s.faqLabel}>{'{ '}FAQ{' }'}</span>
        <div style={s.faqList}>
          {faqs.map((faq, index) => (
            <div key={index} style={s.faqItem}>
              <button
                style={s.faqQ}
                onClick={() => setOpenFaq(openFaq === index ? null : index)}
              >
                <span>{faq.question}</span>
                <span style={s.faqToggle}>
                  {openFaq === index ? '\u2212' : '+'}
                </span>
              </button>
              {openFaq === index && (
                <p style={s.faqA}>{faq.answer}</p>
              )}
            </div>
          ))}
        </div>
      </section>

      <footer style={s.footer}>
        <p style={s.footerRow}>
          <a href="/privacy" className="pr-flink">Privacy Policy</a>
          <span style={s.dot}>&middot;</span>
          <a href="/contact" className="pr-flink">Contact</a>
        </p>
        <p style={{ marginBottom: '12px' }}>
          <a href="mailto:hello@mybracketsim.com" className="pr-flink">
            hello@mybracketsim.com
          </a>
        </p>
        <p style={s.copy}>&copy; 2026 MyBracketSim</p>
      </footer>
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#faf8f5',
    fontFamily: '"Outfit", sans-serif',
    color: '#1c1917',
  },

  /* Header */
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '18px 8%',
    borderBottom: '1px solid #e7e5e4',
  },
  logo: { height: '36px', cursor: 'pointer' },
  nav: { display: 'flex', gap: '24px', alignItems: 'center' },
  navLink: {
    fontFamily: '"Space Mono", monospace',
    fontSize: '0.85rem',
    color: '#1c1917',
    textDecoration: 'none',
    letterSpacing: '0.03em',
  },
  navAccent: {
    fontFamily: '"Space Mono", monospace',
    fontSize: '0.85rem',
    color: '#e8622d',
    textDecoration: 'none',
    letterSpacing: '0.03em',
    padding: '7px 18px',
    border: '1.5px solid #e8622d',
    borderRadius: '3px',
  },

  /* Hero */
  hero: { padding: '90px 8% 50px' },
  heroInner: { maxWidth: '880px', margin: '0 auto', textAlign: 'center' },
  label: {
    fontFamily: '"Space Mono", monospace',
    fontSize: '0.75rem',
    letterSpacing: '0.18em',
    color: '#e8622d',
    display: 'block',
    marginBottom: '20px',
  },
  title: {
    fontFamily: '"Space Mono", monospace',
    fontSize: 'clamp(1.5rem, 3.2vw, 2.2rem)',
    fontWeight: 700,
    lineHeight: 1.4,
    color: '#1c1917',
    margin: 0,
  },

  /* Pricing cards */
  cards: { padding: '20px 8% 70px' },
  cardsGrid: {
    maxWidth: '720px',
    margin: '0 auto',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '24px',
    alignItems: 'start',
  },

  /* Free card */
  freeCard: {
    border: '1px solid #e7e5e4',
    borderRadius: '4px',
    padding: '40px 32px',
    backgroundColor: '#ffffff',
  },
  tierName: {
    fontFamily: '"Space Mono", monospace',
    fontSize: '1.15rem',
    fontWeight: 700,
    color: '#1c1917',
    marginTop: 0,
    marginBottom: '16px',
  },
  priceBlock: { marginBottom: '24px' },
  priceNum: {
    fontFamily: '"Space Mono", monospace',
    fontSize: '2.8rem',
    fontWeight: 700,
    color: '#1c1917',
  },
  feats: {
    listStyle: 'none',
    padding: 0,
    margin: '0 0 28px',
  },
  feat: {
    padding: '8px 0',
    fontSize: '0.95rem',
    color: '#57534e',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
  },
  arrow: {
    color: '#a8a29e',
    fontFamily: '"Space Mono", monospace',
    flexShrink: 0,
    fontSize: '0.85rem',
    marginTop: '2px',
  },
  freeBtn: {
    fontFamily: '"Space Mono", monospace',
    width: '100%',
    padding: '13px 24px',
    fontSize: '0.9rem',
    fontWeight: 700,
    color: '#1c1917',
    backgroundColor: 'transparent',
    border: '1.5px solid #1c1917',
    borderRadius: '3px',
    cursor: 'pointer',
    letterSpacing: '0.02em',
  },

  /* Pro card */
  proCard: {
    backgroundColor: '#1c1917',
    borderRadius: '4px',
    padding: '40px 32px',
  },
  proTier: {
    fontFamily: '"Space Mono", monospace',
    fontSize: '1.15rem',
    fontWeight: 700,
    color: '#faf8f5',
    marginTop: 0,
    marginBottom: '16px',
  },
  proPrice: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '8px',
    marginBottom: '24px',
  },
  proPriceNum: {
    fontFamily: '"Space Mono", monospace',
    fontSize: '2.8rem',
    fontWeight: 700,
    color: '#e8622d',
  },
  proPriceLabel: {
    fontSize: '0.9rem',
    color: '#a8a29e',
  },
  proFeats: {
    listStyle: 'none',
    padding: 0,
    margin: '0 0 28px',
  },
  proFeat: {
    padding: '8px 0',
    fontSize: '0.95rem',
    color: '#d6d3d1',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
  },
  proArrow: {
    color: '#e8622d',
    fontFamily: '"Space Mono", monospace',
    flexShrink: 0,
    fontSize: '0.85rem',
    marginTop: '2px',
  },
  proBtn: {
    fontFamily: '"Space Mono", monospace',
    width: '100%',
    padding: '13px 24px',
    fontSize: '0.9rem',
    fontWeight: 700,
    color: '#ffffff',
    backgroundColor: '#e8622d',
    border: 'none',
    borderRadius: '3px',
    cursor: 'pointer',
    letterSpacing: '0.02em',
  },

  /* FAQ */
  faqSection: {
    padding: '70px 8%',
    borderTop: '1px solid #e7e5e4',
  },
  faqLabel: {
    fontFamily: '"Space Mono", monospace',
    fontSize: '0.75rem',
    letterSpacing: '0.18em',
    color: '#e8622d',
    display: 'block',
    marginBottom: '32px',
    textAlign: 'center',
  },
  faqList: {
    maxWidth: '660px',
    margin: '0 auto',
  },
  faqItem: {
    borderTop: '1px solid #e7e5e4',
  },
  faqQ: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '18px 0',
    background: 'none',
    border: 'none',
    fontFamily: '"Space Mono", monospace',
    fontSize: '0.95rem',
    fontWeight: 700,
    color: '#1c1917',
    cursor: 'pointer',
    textAlign: 'left',
    letterSpacing: '0.01em',
  },
  faqToggle: {
    fontFamily: '"Space Mono", monospace',
    fontSize: '1.2rem',
    color: '#e8622d',
    flexShrink: 0,
    marginLeft: '14px',
  },
  faqA: {
    fontSize: '0.95rem',
    color: '#57534e',
    lineHeight: 1.7,
    paddingBottom: '18px',
    margin: 0,
    fontFamily: '"Outfit", sans-serif',
  },

  /* Footer */
  footer: {
    padding: '36px 8%',
    textAlign: 'center',
    borderTop: '1px solid #e7e5e4',
  },
  footerRow: { marginBottom: '10px' },
  dot: { margin: '0 10px', color: '#a8a29e' },
  copy: { color: '#a8a29e', fontSize: '0.8rem', margin: 0 },
};

/* Load fonts */
if (!document.querySelector('#bracketsim-fonts')) {
  const link = document.createElement('link');
  link.id = 'bracketsim-fonts';
  link.rel = 'stylesheet';
  link.href =
    'https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700&family=Space+Mono:wght@400;700&display=swap';
  document.head.appendChild(link);
}

/* Interaction styles */
if (!document.querySelector('#pricing-styles')) {
  const sheet = document.createElement('style');
  sheet.id = 'pricing-styles';
  sheet.textContent = `
    .pr-pro-btn:hover { background-color: #c2410c !important; }
    .pr-free-btn:hover {
      background-color: #1c1917 !important;
      color: #faf8f5 !important;
    }
    .pr-login-link:hover {
      background-color: #e8622d !important;
      color: #fff !important;
    }
    .pr-flink {
      color: #78716c;
      text-decoration: none;
      font-size: 0.9rem;
    }
    .pr-flink:hover { color: #e8622d; }
  `;
  document.head.appendChild(sheet);
}

export default Pricing;
